import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Leaf } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function AuthPage({ mode = "login" }) {
    const isSignup = mode === "signup";
    const { loginWithCredentials, signup } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", password: "", name: "" });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            if (isSignup) {
                const u = await signup(form.email, form.password, form.name);
                toast.success("Welcome to EDEN.");
                navigate(u.onboarded ? "/dashboard" : "/onboarding");
            } else {
                const u = await loginWithCredentials(form.email, form.password);
                toast.success("Welcome back.");
                navigate(u.onboarded ? "/dashboard" : "/onboarding");
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Something went wrong.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground grain flex">
            <div className="hidden lg:block lg:w-1/2 relative">
                <img
                    src="https://images.unsplash.com/photo-1523248948644-586f1ab2a83e?w=1600&auto=format&fit=crop&q=80"
                    alt="Serene landscape"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-black/60 to-transparent" />
                <div className="relative z-10 p-12 h-full flex flex-col justify-between">
                    <Link to="/" className="flex items-center gap-2 text-white" data-testid="auth-logo">
                        <Leaf className="w-5 h-5 text-[hsl(var(--eden-gold))]" strokeWidth={1.5} />
                        <span className="font-serif text-2xl tracking-wide">EDEN</span>
                    </Link>
                    <div className="text-white max-w-md">
                        <div className="font-serif text-4xl leading-tight">
                            "The LORD God took the man, and put him into the garden of Eden to dress it and to keep it."
                        </div>
                        <div className="text-white/70 mt-4 text-sm tracking-wide">Genesis 2:15</div>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6 md:p-12 relative z-[2]">
                <div className="w-full max-w-md">
                    <div className="mb-10">
                        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">
                            {isSignup ? "Begin the Blueprint" : "Welcome back"}
                        </div>
                        <h1 className="font-serif text-4xl md:text-5xl leading-[1.05]">
                            {isSignup ? "Tend what has been entrusted." : "Return to the garden."}
                        </h1>
                    </div>

                    <button
                        onClick={googleLogin}
                        data-testid="google-login-btn"
                        className="w-full h-11 border border-foreground/15 rounded-md text-sm hover:bg-foreground/[0.04] transition-colors flex items-center justify-center gap-3"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                        Continue with Google
                    </button>

                    <div className="my-6 flex items-center gap-4 text-xs text-muted-foreground uppercase tracking-widest">
                        <div className="flex-1 h-px bg-foreground/10" />
                        <span>or</span>
                        <div className="flex-1 h-px bg-foreground/10" />
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        {isSignup && (
                            <div>
                                <Label htmlFor="name" className="text-xs uppercase tracking-widest text-muted-foreground">Name</Label>
                                <Input
                                    id="name"
                                    data-testid="signup-name-input"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    required
                                    className="mt-2 h-11 rounded-md border-foreground/15 bg-transparent"
                                />
                            </div>
                        )}
                        <div>
                            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                data-testid="auth-email-input"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                required
                                className="mt-2 h-11 rounded-md border-foreground/15 bg-transparent"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                data-testid="auth-password-input"
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                required
                                minLength={6}
                                className="mt-2 h-11 rounded-md border-foreground/15 bg-transparent"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={busy}
                            data-testid="auth-submit-btn"
                            className="w-full h-11 bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md"
                        >
                            {busy ? "…" : isSignup ? "Create account" : "Sign in"}
                        </Button>
                    </form>

                    <div className="mt-8 text-sm text-muted-foreground text-center">
                        {isSignup ? (
                            <>Already have an account? <Link to="/login" className="text-foreground underline underline-offset-4">Sign in</Link></>
                        ) : (
                            <>New to EDEN? <Link to="/signup" className="text-foreground underline underline-offset-4">Create an account</Link></>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
